import { useEffect, useRef, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../stores/auth-store";
import { MODULES } from "@kubdee/shared/module-ids";
import { toast } from "sonner";

export type WebSocketStatus = "connecting" | "connected" | "disconnected" | "error";

export interface PlatformWsEvent {
  type:
    // Chat events (existing)
    | "new_message"
    | "conversation_updated"
    | "conversation_created"
    | "typing"
    // Notification events (new)
    | "notification"
    | "notification_count"
    // System
    | "pong";
  companyId?: string;
  conversationId?: string;
  data?: Record<string, unknown>;
  timestamp?: string;
}

export function usePlatformWebSocket() {
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.currentCompanyId);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const reconnectAttempt = useRef(0);
  const [status, setStatus] = useState<WebSocketStatus>("disconnected");

  const connect = useCallback(async () => {
    if (!companyId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      // Get short-lived WS token
      const { getBaseUrl } = await import("@kubdee/sdk/api-client");
      const apiBase = getBaseUrl();
      const tokenRes = await fetch(`${apiBase}/platform/auth/ws-token`, {
        credentials: "include",
      });
      if (!tokenRes.ok) {
        setStatus("error");
        return;
      }
      const { data } = await tokenRes.json();
      const token = data?.token;
      if (!token) {
        setStatus("error");
        return;
      }

      // Build WS URL
      const wsBase = apiBase.replace(/^http/, "ws");
      const url = `${wsBase}/ws?token=${encodeURIComponent(token)}&companyId=${encodeURIComponent(companyId)}`;

      const ws = new WebSocket(url);
      wsRef.current = ws;
      setStatus("connecting");

      ws.onopen = () => {
        setStatus("connected");
        reconnectAttempt.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const wsEvent: PlatformWsEvent = JSON.parse(event.data);
          handleEvent(wsEvent);
        } catch { /* ignore invalid JSON */ }
      };

      ws.onclose = () => {
        setStatus("disconnected");
        wsRef.current = null;
        // Exponential backoff with jitter: 1s, 2s, 4s, 8s, max 15s
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current), 15000);
        const jitter = Math.random() * 1000;
        reconnectAttempt.current++;
        reconnectTimer.current = setTimeout(connect, delay + jitter);
      };

      ws.onerror = () => {
        setStatus("error");
        ws.close();
      };
    } catch {
      setStatus("error");
    }
  }, [companyId]);

  const handleEvent = useCallback((event: PlatformWsEvent) => {
    switch (event.type) {
      // ── Chat events ──
      case "new_message":
        queryClient.invalidateQueries({ queryKey: [MODULES.CHAT, "conversations"] });
        if (event.conversationId) {
          queryClient.invalidateQueries({
            queryKey: [MODULES.CHAT, "conversations", event.conversationId]
          });
        }
        break;

      case "conversation_created":
        queryClient.invalidateQueries({ queryKey: [MODULES.CHAT, "conversations"] });
        break;

      case "conversation_updated":
        queryClient.invalidateQueries({ queryKey: [MODULES.CHAT, "conversations"] });
        if (event.conversationId) {
          queryClient.invalidateQueries({
            queryKey: [MODULES.CHAT, "conversations", event.conversationId]
          });
        }
        break;

      // ── Notification events ──
      case "notification":
        queryClient.invalidateQueries({ queryKey: ["core", "notifications"] });
        queryClient.invalidateQueries({ queryKey: ["core", "notifications", "unread-count"] });
        // Show toast notification
        if (event.data?.title) {
          toast(event.data.title as string, {
            description: event.data.message as string,
          });
        }
        break;

      case "notification_count":
        queryClient.invalidateQueries({ queryKey: ["core", "notifications", "unread-count"] });
        break;

      // ── System ──
      case "pong":
        // Heartbeat response — no action needed
        break;
    }
  }, [queryClient]);

  // Send heartbeat every 30s to keep connection alive
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Connect on mount, cleanup on unmount
  useEffect(() => {
    connect();
    return () => {
      wsRef.current?.close();
      clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  return { status, reconnect: connect };
}
